import AdminPostList from "../components/admin/AdminPostList";
import useAdminOperation from "../hooks/useAdminOperation";
import "./AdminSecondary.css";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

export default function SocialAccounts() {
  const beginOperation = useAdminOperation();
  const [accounts, setAccounts] = useState([]);
  const [health, setHealth] = useState(null);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState({ gallery_facebook: false, gallery_instagram: false, event_facebook: false, event_instagram: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState("");

  const load = useCallback(async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setLoading(true);
    try {
      const [accountsRes, healthRes, postsRes, settingsRes] = await Promise.all([
        api.get("/admin/social/accounts"),
        api.get("/admin/social/meta/health"),
        api.get("/admin/social/posts"),
        api.get("/admin/social/settings"),
      ]);
      if (!operationCurrent()) return;
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      setHealth(healthRes.data || null);
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
      setSettings((current) => settingsRes.data || current);
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setLoading(false);
      }
    }
  }, [beginOperation]);

  useEffect(() => {
    load();
  }, [load]);

  const connectMeta = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setConnecting(true);
    try {
      const res = await api.get("/admin/social/meta/connect");
      if (!operationCurrent()) return;
      if (res.data?.authorize_url) {
        window.open(res.data.authorize_url, "_blank", "noopener,noreferrer");
        toast.success("Finish connecting in the Meta window, then refresh this page.");
      }
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setConnecting(false);
      }
    }
  };

  const disconnect = async (platform) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const account = accounts.find((a) => a.platform === platform);
    if (!account?.id) return;
    if (!window.confirm(`Disconnect the ${platform} account?`)) return;
    try {
      await api.delete(`/admin/social/accounts/${account.id}`);
      if (!operationCurrent()) return;
      toast.success(`${platform} disconnected.`);
      await load();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const testPost = async (platform) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const account = accounts.find((a) => a.platform === platform);
    if (!account?.id) return;
    setTesting(platform);
    try {
      await api.post(`/admin/social/accounts/${account.id}/test`);
      if (!operationCurrent()) return;
      toast.success(`${platform} connection verified.`);
      await load();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    } finally {
      if (operationCurrent()) {
      setTesting("");
      }
    }
  };

  const retryPost = async (postId) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    try {
      await api.post(`/admin/social/posts/${postId}/retry`);
      if (!operationCurrent()) return;
      toast.success("Retry queued.");
      await load();
      if (!operationCurrent()) return;
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const updateSetting = async (key, value) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    try {
      const res = await api.put("/admin/social/settings", { [key]: value });
      if (!operationCurrent()) return;
      setSettings(res.data);
      toast.success("Auto-post settings saved.");
    } catch (err) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(err));
    }
  };

  const fbAccount = accounts.find((a) => a.platform === "facebook");
  const igAccount = accounts.find((a) => a.platform === "instagram");
  const fbConnected = Boolean(fbAccount);
  const igConnected = Boolean(igAccount);
  const fbHealth = health?.facebook || {};
  const igHealth = health?.instagram || {};

  const Toggle = ({ label, value, onChange, testId }) => {
    const available = typeof value === "boolean";
    return (
    <label className="admin-social-switch-row flex items-center justify-between gap-3 text-sm text-stone-300">
      <span>{label}{!available && <small role="status" className="admin-setting-unavailable">Unavailable — refresh settings before changing this.</small>}</span>
      <button
        type="button"
        role="switch"
        aria-label={available ? label : `${label} unavailable`}
        aria-checked={available ? value : false}
        disabled={!available}
        data-testid={testId}
        onClick={() => { if (available) onChange(!value); }}
        className={`admin-social-switch h-6 w-11 rounded-full border transition ${value ? "border-emerald-500/50 bg-emerald-500/20" : "border-stone-100/15 bg-zinc-950"}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-[#F5F0E6] transition ${value ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </label>
  );
  };

  const AccountCard = ({ platform, title, account, health: h }) => {
    const hasAccount = Boolean(account);
    const storedStatusAllowsConnection = account?.connectionStatus == null || account.connectionStatus === "connected";
    const connected = hasAccount && storedStatusAllowsConnection && h?.connected === true && h?.reconnect_required !== true;
    const needsReconnect = hasAccount && (h?.connected === false || h?.reconnect_required === true || !storedStatusAllowsConnection);
    const connectionLabel = connected ? "Connected" : !hasAccount ? "Not connected" : needsReconnect ? "Needs reconnect" : "Connection unavailable";
    return (
      <article className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-stone-200 border-stone-100/15 bg-stone-100/5">
            {title}
          </div>
          <span data-testid={`social-connection-${platform}`} className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${connected ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-stone-500/40 bg-stone-500/10 text-stone-400"}`}>
            {connectionLabel}
          </span>
        </div>
        <div className="mt-5">
          <p className="font-display text-xl text-stone-100">{hasAccount ? (account.accountName || "Beyond Board Woodworking") : "Not connected"}</p>
          {platform === "instagram" && hasAccount ? (
            <p className="text-sm text-stone-400">{account.accountHandle}</p>
          ) : null}
          {hasAccount && h ? (
            <div className="mt-3 space-y-1 text-xs text-stone-400">
              {platform === "facebook" && h.page_name ? <p>Page: {h.page_name}</p> : null}
              {platform === "instagram" && h.account_type ? <p>Account type: {h.account_type}</p> : null}
              {h.reconnect_required ? <p className="text-amber-300">Reconnect required</p> : null}
            </div>
          ) : null}
        </div>
        <div className="mt-6 grid gap-2">
          {!hasAccount ? (
            <Button onClick={connectMeta} disabled={connecting} className="bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C]">
              {connecting ? "Connecting..." : "Connect via Meta"}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={connectMeta} disabled={connecting} className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
                Reconnect
              </Button>
              <Button variant="outline" onClick={() => disconnect(platform)} className="bg-transparent border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
                Disconnect
              </Button>
              <Button variant="outline" onClick={() => testPost(platform)} disabled={testing === platform} className="bg-transparent border-[#D8A34B]/40 text-[#D8A34B] hover:bg-[#D8A34B]/10">
                {testing === platform ? "Testing..." : "Test Post"}
              </Button>
            </>
          )}
        </div>
        {platform === "instagram" && fbConnected && !igConnected ? (
          <p className="mt-3 text-xs text-amber-300">Connect via the Facebook Page to link the Instagram Professional account.</p>
        ) : null}
      </article>
    );
  };

  const StatusBadge = ({ status }) => {
    const map = { PUBLISHED: "text-emerald-300", FAILED: "text-rose-300", QUEUED: "text-amber-300", PUBLISHING: "text-amber-300", SCHEDULED: "text-stone-300", CANCELLED: "text-stone-500" };
    return <span className={map[status] || "text-stone-300"}>{status === "PUBLISHED" ? "Posted ✓" : status === "FAILED" ? "Failed ⚠" : status}</span>;
  };

  return (
    <div className="bbw-admin-secondary min-h-screen" data-testid="social-accounts-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-2">Settings</p>
          <h1 className="font-display text-4xl text-[#F5F0E6]">Social Accounts</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin">
            <Button variant="outline" className="bg-transparent border-stone-100/15 text-stone-200 hover:bg-stone-100/5">
              Back to dashboard
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <p className="mb-4 text-sm text-stone-400">Connect both accounts with one Meta login. Instagram publishing uses the Facebook-linked Professional account.</p>
        <div className="grid gap-6 lg:grid-cols-2">
          <AccountCard platform="facebook" title="Facebook Page" account={fbAccount} health={fbHealth} />
          <AccountCard platform="instagram" title="Instagram Professional" account={igAccount} health={igHealth} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <div className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
          <h2 className="font-display text-2xl text-stone-100">Auto Post</h2>
          <p className="mt-1 text-sm text-stone-400">Automatically post new gallery pieces and events. All off by default until you enable them.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500">New Gallery Piece</p>
              <Toggle label="Facebook" value={settings.gallery_facebook} onChange={(v) => updateSetting("gallery_facebook", v)} testId="autopost-gallery-facebook" />
              <Toggle label="Instagram" value={settings.gallery_instagram} onChange={(v) => updateSetting("gallery_instagram", v)} testId="autopost-gallery-instagram" />
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500">New Event</p>
              <Toggle label="Facebook" value={settings.event_facebook} onChange={(v) => updateSetting("event_facebook", v)} testId="autopost-event-facebook" />
              <Toggle label="Instagram" value={settings.event_instagram} onChange={(v) => updateSetting("event_instagram", v)} testId="autopost-event-instagram" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <h2 className="font-display text-2xl text-stone-100">Publishing activity</h2>
        {posts.length === 0 ? (
          <p className="mt-3 text-sm text-stone-400">No social posts yet.</p>
        ) : (
          <div className="mt-4 space-y-3">
            <AdminPostList posts={posts}>{(post) => (
              <article key={post.id} className="rounded-xl border border-stone-100/10 bg-zinc-900/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-100 truncate">{post.sourceType === "EVENT" ? "Event" : "Gallery"} — {post.caption?.split("\n")[0]}</p>
                    <p className="text-xs text-stone-500 mt-1">{post.createdAt ? new Date(post.createdAt).toLocaleString() : ""}</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-stone-400">{post.platform}</span>
                    <StatusBadge status={post.status} />
                    {post.status === "FAILED" && (
                      <Button size="sm" variant="outline" onClick={() => retryPost(post.id)} className="bg-transparent border-stone-100/15 text-stone-200">
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
                {post.status === "FAILED" && post.failureMessage ? (
                  <p className="mt-2 text-xs text-rose-300">{post.failureMessage}</p>
                ) : null}
              </article>
            )}</AdminPostList>
          </div>
        )}
      </section>

      {loading && <div className="max-w-7xl mx-auto px-6 pb-6 text-stone-400">Loading accounts…</div>}
    </div>
  );
}

